import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { fetchContentJson, GameName, PageKind } from "inbox-contentful";

import { setGameData } from "../../../contexts/game/actions/set-game-data";

import { useLocalizedNamespace } from "../../../hooks/use-localized-namespace";

const MAX_PREVIEW_CAROUSEL_ENTRIES = 6;

type WithContentfulProps = {
    context: {
        state: { settings: { game: Record<string, unknown> } };
        dispatch: (action: unknown) => void;
    };
    namespace: string;
    locale: { lang: string };
};

export const withContentful =
    <P extends WithContentfulProps>(
        Component: React.ComponentType<P & { data: unknown; error?: Error }>,
    ) =>
    (props: P) => {
        const { context, namespace, locale } = props;

        const { lang } = locale;
        const { state, dispatch } = context;
        const { settings } = state;
        const { game } = settings;
        const localizedNamespace = `${lang}-${namespace}`;
        const data = game[localizedNamespace];

        const [searchParams] = useSearchParams();
        const preview = searchParams.has("preview");
        const env =
            searchParams.get("env") ?? window.CONTENTFUL_CONFIG?.environment;

        const carouselId = searchParams.get("openCarouselId");

        const gameName = GameName.parse(window.GAMENAME);

        const [error, setError] = React.useState();

        const [, setLocalizedNamespace] = useLocalizedNamespace();

        // Related media URLs come without protocol, we need to
        // add it to make the images work in-game with localhost
        const cdnTransformFunction = window.IS_DEV
            ? (url: string): string =>
                  url.startsWith("//") ? `https:${url}` : url
            : (url: string): string =>
                  url.replace("https://images.ctfassets.net", "");

        const onError = setError;

        const onLoad = (raw: unknown) => {
            setLocalizedNamespace(localizedNamespace);
            dispatch(setGameData(localizedNamespace, raw));
        };

        useEffect(() => {
            setError(undefined);
            if (!window.CONTENTFUL_CONFIG) return;

            if (!data)
                fetchContentJson(
                    lang,
                    gameName,
                    PageKind.parse(namespace),
                    {
                        ...window.CONTENTFUL_CONFIG,
                        environment: env as string,
                        preview,

                        // If we have a preview carousel id, we want to fetch all entries to make it show up
                        alwaysIncludedCarouselEntries: carouselId
                            ? [carouselId]
                            : [],

                        carouselEntryLimit: preview
                            ? MAX_PREVIEW_CAROUSEL_ENTRIES
                            : undefined,
                    },

                    { transformAssetPath: cdnTransformFunction },
                )
                    .then((v) => onLoad(v))
                    .catch((err) => onError(err));
        }, [lang]);

        return <Component {...props} data={data} error={error} />;
    };
